import React, { useEffect } from 'react';
import {Bar} from 'react-chartjs-2';
import ApiService from '../../services/apiService';
import { useOktaAuth } from '@okta/okta-react';
import { Api } from '../../types/apiData';
import { count } from 'console';
import { main } from '@popperjs/core';
import { useNavigate } from 'react-router-dom';
import { useDarkTheme } from '../../contexts/DarkThemeContext';
import { BUNIT_COLORS, BUNIT_COLORS_DARK } from './shared/chartColors';

const ApiByBUnit: React.FC = () => {
    const [businessUnitCounts, setBusinessUnitCounts] = React.useState<Map<string, number>>(new Map());
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const { isDarkTheme } = useDarkTheme();
    const [apiData, setApiData] = React.useState<any[]>([]);
    const [chartData, setChartData] = React.useState<number[]>([]);
    const [chartLabels, setChartLabels] = React.useState<string[]>([]);

    const handleChartClick = (event: any, elements: any[]) => {
        if (elements.length > 0) {
            const clickedIndex = elements[0].index;
            const clickedLabel = chartLabels[clickedIndex];
            console.log('Chart clicked:', clickedLabel);
            
            // Create URL parameters based on the clicked business unit
            const params = new URLSearchParams();
            
            // Find the area hierarchy that matches this business unit
            const matchingApi = apiData.find(api => {
                const businessUnitName = api.areas && api.areas.find((area: any) => area.level === 1)?.name;
                return businessUnitName === clickedLabel;
            });
            
            if (matchingApi && matchingApi.areas) {
                const businessUnitArea = matchingApi.areas.find((area: any) => area.level === 1);
                if (businessUnitArea) {
                    params.set('areaId', businessUnitArea.id.toString());
                }
            }
            
            // Navigate to table view with filters
            navigate(`/table-view?${params.toString()}`);
        }
    };

    const extractData = (apiData: any[]) => {
        console.log("Extracting data from:", apiData);
        
        //Add a property name and return the processed data
        const processedData = apiData.map((api) => {
            const businessUnit = api.areas && api.areas.find((area: any) => area.level === 1)?.name || '';
            //console.log(`API ID: ${api.id || 'unknown'}, Found business unit: ${businessUnit}`);
            return { businessUnit };
        });
        
        console.log("Extraction complete, processed data:", processedData);
        return processedData;
    }

    function countBusinessUnit<B>(array: B[], keyExtractor: (item: B) => string): Map<string, number> {
        const counts = new Map<string, number>();
        array.forEach(item => {
            const key = keyExtractor(item);
            if (key) { // Only count if key exists
                // Increment the count for the key
                counts.set(key, (counts.get(key) || 0) + 1);
            }
        });
        
        return counts;
    }

    const processMapData = (countsMap: Map<string, number>) => {
        console.log("Processing map data for chart:", countsMap);
        const labels: string[] = [];
        const values: number[] = [];
        
        // Convert Map entries to arrays for the chart
        countsMap.forEach((count, businessUnit) => {
            if (businessUnit) { // Only include non-empty business units
                labels.push(businessUnit);
                values.push(count);
            }
        });
        
        setChartLabels(labels);
        setChartData(values);
        console.log("Chart data prepared:", { labels, values });
    }

    const getData = async() => {
        try {
            const apiService = new ApiService(); // Create an instance
            const data = (await apiService.getAllApis(authState)) as any[];
            console.log("Raw API data fetched:", data);
            setApiData(data);
            
            const processed = extractData(data);
            console.log("Processed API data: ", processed);
            const counts = countBusinessUnit(processed, (item) => item.businessUnit);
            console.log("Business Unit Counts: ", counts);
            setBusinessUnitCounts(counts);
            processMapData(counts);
        } catch (error) {
            console.error("Error fetching API data for the Graphics:", error);
        } 
    }

    useEffect(() => {
        getData();
    }, [authState]);

    useEffect(() => {
        if (businessUnitCounts.size > 0) {
            processMapData(businessUnitCounts);
        }
    }, [businessUnitCounts]);

    const textColor = isDarkTheme ? 'rgba(255, 255, 255, 0.85)' : undefined;
    const tickColor = isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : undefined;
    const gridColor = isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : undefined;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleChartClick,
        onHover: (event: any, elements: any[]) => {
            event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        },
        plugins: {
            title: {
                display: true,
                text: 'API by Business Unit',
                color: textColor,
                font: {
                    size: 20,
                },
                padding: {
                    top: 10,
                    bottom:20,
                }
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                ticks: {
                    color: tickColor,
                    font: {
                        size: 10,
                    }
                },
                grid: {
                    color: gridColor,
                }
            },
            y: {
                ticks: {
                    color: tickColor,
                    font: {
                        size: 10,
                    },
                    precision: 0,
                },
                grid: {
                    color: gridColor,
                },
                beginAtZero: true,
            }
        }
    }

    const labels = chartLabels;
    
    const data = {
        labels,
        datasets: [
            {
                label: '', // Empty string instead of 'API Count'
                data: chartData,
                backgroundColor: isDarkTheme ? BUNIT_COLORS_DARK.BACKGROUND : BUNIT_COLORS.BACKGROUND,
                borderColor: isDarkTheme ? BUNIT_COLORS_DARK.BORDER : BUNIT_COLORS.BORDER,
                borderWidth: 1,
            },
        ],
    }

    return (
        <div className="graphic-content">
            <Bar options={options} data={data} />
        </div>
    )
}

export default ApiByBUnit;